import React, { useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  Button,
  Stack,
  InputBase,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { tokens } from "../../../../../theme";
import { ICommentType } from "./tabs/MemoriesTab";
import CustomTextField from "../../../../../components/common/Textfield";

interface CommentCardProps {
  comment: ICommentType;
  isAdmin: boolean;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  handleReplyToggle: (id: string) => void;
  replyStates: { [commentId: string]: boolean };
  handleReplyChange: (id: string, value: string) => void;
  replyTexts: { [commentId: string]: string };
  handleReplySubmit: (
    parentId: string,
    replyText: string,
    authorName: string
  ) => void;
  colors: ReturnType<typeof tokens>;
  isMobile: boolean;
  messages: ICommentType[];
  depth?: number;
}

const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  isAdmin,
  handleApprove,
  handleDelete,
  handleReplyToggle,
  replyStates,
  handleReplyChange,
  replyTexts,
  handleReplySubmit,
  colors,
  isMobile,
  messages,
  depth = 0,
}) => {
  const [open, setOpen] = useState(false);
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dialogReply, setDialogReply] = useState("");
  const [saveDetails, setSaveDetails] = useState(false);

  if (!comment.id) return null;

  const handleOpenDialog = () => {
    setDialogReply(replyTexts[comment.id] || "");
    setFirstName(localStorage.getItem("commentFirstName") || "");
    setLastName(localStorage.getItem("commentLastName") || "");
    setEmail(localStorage.getItem("commentEmail") || "");
    setOpen(true);
  };

  const handleSave = () => {
    const authorName = `${first_name} ${last_name}`.trim() || "Anonymous";
    handleReplySubmit(comment.id, dialogReply, authorName);

    if (saveDetails) {
      localStorage.setItem("commentFirstName", first_name);
      localStorage.setItem("commentLastName", last_name);
      localStorage.setItem("commentEmail", email);
    }

    setOpen(false);
  };

  const author = comment.author || "Anonymous";
  const avatarInitials =
    author
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0].toUpperCase())
      .join("") || "A";

  const parentMessage = comment.parent_message
    ? messages.find((m) => m.id === comment.parent_message)
    : null;

  const avatarColors = [
    colors.primary[500],
    colors.primary[900],
    colors.neutral[300],
    colors.neutral[100],
    colors.secondary[900],
    colors.primary[600],
  ];
  const avatarBgColor =
    avatarColors[Math.floor(Math.random() * avatarColors.length)];

  const replyAvatarInitials =
    `${first_name[0] || ""}${last_name[0] || ""}`.toUpperCase() || "A";

  return (
    <>
      <Box
        sx={{
          ml: { xs: `${depth * 16}px`, sm: `${depth * 24}px` },
          mt: 1.5,
          borderLeft:
            depth > 0
              ? {
                  xs: `2px solid ${colors.primary[800]}`,
                  sm: `2px solid ${colors.primary[800]}`,
                }
              : "none",
          pl: depth > 0 ? { xs: 1, sm: 1.5 } : 0,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {parentMessage && (
          <Box
            sx={{
              mb: 1,
              p: 1,
              borderLeft: `3px solid ${colors.primary[300]}`,
              backgroundColor: "transparent",
              borderRadius: "4px",
              cursor: "pointer",
              "&:hover": { backgroundColor: colors.grey[800] },
            }}
            onClick={() => {
              const parentElement = document.getElementById(
                `message-${parentMessage.id}`
              );
              if (parentElement) {
                parentElement.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "brown", fontStyle: "italic", display: "block" }}
            >
              Replying to {parentMessage.author || "Anonymous"}: "
              {parentMessage.content.length > 50
                ? parentMessage.content.substring(0, 50) + "..."
                : parentMessage.content}
              "
            </Typography>
          </Box>
        )}

        {/* Conditional layout */}
        {!isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0.5, sm: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: { xs: 20, sm: 24, md: 28 },
                    height: { xs: 20, sm: 24, md: 28 },
                    bgcolor: avatarBgColor,
                    color: colors.primary[100],
                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  }}
                >
                  {avatarInitials}
                </Avatar>
                <Typography
                  sx={{
                    color: colors.primary[100],
                    fontSize: { xs: "0.7rem", sm: "0.7rem", md: "0.8rem" },
                  }}
                >
                  {author}
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: colors.neutral[600],
                  fontSize: { xs: "0.7rem", sm: "0.7rem" },
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                {comment.date}
              </Typography>
            </Box>

            <Box
              sx={{ ml: { xs: `calc(24px + 4px)`, sm: `calc(32px + 8px)` } }}
            >
              <Typography
                sx={{
                  lineHeight: 1.6,
                  fontSize: { xs: "0.9rem", sm: "0.9rem" },
                  color: colors.neutral[600],
                }}
              >
                {comment.content}
              </Typography>

              <Box
                mt={0.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  onClick={() => handleReplyToggle(comment.id)}
                  sx={{
                    color: colors.primary[400],
                    fontSize: { xs: "0.65rem", sm: "0.65rem" },
                    padding: "1px 4px",
                    minWidth: "auto",
                    height: 20,
                    lineHeight: 1.1,
                    textTransform: "none",
                  }}
                >
                  Reply
                </Button>

                {isAdmin && comment.id && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ mt: { xs: 1, sm: 0 } }}
                  >
                    {!comment.is_approved && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: { xs: "0.6rem", sm: "0.6rem" } }}
                      >
                        Pending
                      </Typography>
                    )}
                    <Button
                      onClick={() =>
                        handleApprove(comment.id, !comment.is_approved)
                      }
                      variant="outlined"
                      sx={{
                        borderColor: colors.primary[300],
                        color: colors.primary[100],
                        fontSize: { xs: "0.6rem", sm: "0.6rem" },
                        padding: "1px 4px",
                        minWidth: "auto",
                        height: 20,
                        lineHeight: 1.1,
                        textTransform: "none",
                      }}
                    >
                      {comment.is_approved ? "Disapprove" : "Approve"}
                    </Button>
                    <Button
                      onClick={() => handleDelete(comment.id)}
                      variant="outlined"
                      color="error"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.65rem" },
                        padding: "1px 4px",
                        minWidth: "auto",
                        height: 20,
                        lineHeight: 1.1,
                        textTransform: "none",
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          // MOBILE VIEW (admin buttons right-aligned)
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: colors.primary[100],
                fontSize: "0.75rem",
              }}
            >
              {author}
            </Typography>
            <Typography
              sx={{
                color: colors.neutral[600],
                fontSize: "0.65rem",
                mb: 0.5,
              }}
            >
              {comment.date}
            </Typography>
            <Typography
              sx={{
                lineHeight: 1.6,
                fontSize: "0.7rem",
                color: colors.neutral[600],
                mb: 0.5,
              }}
            >
              {comment.content}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              width="100%"
            >
              <Button
                onClick={() => handleReplyToggle(comment.id)}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  padding: "1px 4px",
                  minWidth: "auto",
                  height: 20,
                }}
              >
                Reply
              </Button>

              <Box
                sx={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {!comment.is_approved && (
                  <Typography
                    variant="caption"
                    color="orange"
                    fontSize="0.65rem"
                  >
                    Pending
                  </Typography>
                )}
                {isAdmin && (
                  <>
                    <Button
                      onClick={() =>
                        handleApprove(comment.id, !comment.is_approved)
                      }
                      variant="outlined"
                      sx={{
                        fontSize: "0.65rem",
                        textTransform: "none",
                        padding: "1px 4px",
                      }}
                    >
                      {comment.is_approved ? "Disapprove" : "Approve"}
                    </Button>
                    <Button
                      onClick={() => handleDelete(comment.id)}
                      variant="outlined"
                      color="error"
                      sx={{
                        fontSize: "0.65rem",
                        textTransform: "none",
                        padding: "1px 4px",
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </Box>
            </Stack>
          </Box>
        )}

        {/* Reply input */}
        {replyStates[comment.id] && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 1, sm: 1.5 },
              width: "100%",
              mt: 1.5,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "warning.light",
                color: "#fff",
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                flexShrink: 0,
              }}
            >
              {replyAvatarInitials}
            </Avatar>

            <Box
              sx={{
                backgroundColor: colors.primary[800],
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                flex: 1,
                px: 1,
              }}
            >
              <InputBase
                placeholder="Write a reply..."
                value={replyTexts[comment.id] || ""}
                onChange={(e) => handleReplyChange(comment.id, e.target.value)}
                sx={{ flex: 1, color: colors.primary[100] }}
              />
            </Box>

            <IconButton
              onClick={handleOpenDialog}
              sx={{
                color: "#fff",
                backgroundColor: "info.main",
                "&:hover": { backgroundColor: "info.light" },
                flexShrink: 0,
              }}
            >
              <Send />
            </IconButton>
          </Box>
        )}

        {comment.replies && comment.replies.length > 0 && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {comment.replies.map((reply) => (
              <CommentCard
                key={reply.id}
                comment={reply}
                isAdmin={isAdmin}
                handleApprove={handleApprove}
                handleDelete={handleDelete}
                handleReplyToggle={handleReplyToggle}
                replyStates={replyStates}
                handleReplyChange={handleReplyChange}
                replyTexts={replyTexts}
                handleReplySubmit={handleReplySubmit}
                colors={colors}
                isMobile={isMobile}
                messages={messages}
                depth={depth + 1}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle>Enter Your Details</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            margin="dense"
            label="First Name"
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Surname"
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Your Reply"
            value={dialogReply}
            onChange={(e) => setDialogReply(e.target.value)}
            multiline
            rows={4}
            required
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={saveDetails}
                onChange={(e) => setSaveDetails(e.target.checked)}
              />
            }
            label="Save my details for next time"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            sx={{ backgroundColor: colors.primary[800] }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentCard;
